import { getRangeRatio, interpolateVector } from '../helpers/MotionHelper'
import { ease, Easing } from '../helpers/EaseHelper'

const DEFAULT_DURATION = 5500
class MotionCam {

  constructor (THREECamera) {
    this.cam = THREECamera
    this.datas = null
    this.dataIntro = null
    this.dataOutro = null
    this.duration = 0
    this.ratio = 0
    this.smoothRatio = 0
    this.dataIndexCurrent = 0
    this.position = this.cam.position
    this.rotation = this.cam.rotation
    this.target = { x: 0, y: 0, z: 0 }
    this.offset = { x: 0, y: 0 }
    this.setPosition(0, 2, 12)
    this.setTarget(0, 0, 0)
    this.setRotation(0, 0, 0)
    this.needUpdate = false
  }

  setPosition (x, y, z, velocity = 1) {
    this.position.x = x
    this.position.y = y
    this.position.z = z
  }

  setTarget (x, y, z, velocity = 1) {
    this.target.x = x
    this.target.y = y
    this.target.z = z
    this.cam.lookAt(this.target)
  }

  setRotation (x, y, z, velocity = 1) {
    this.rotation.x = x
    this.rotation.y = y
    this.rotation.z = z
  }

  lookAt (vector) {
    this.cam.lookAt(vector)
  }

  setAnimation (datas, duration) {
    this.dataIndexCurrent = 0
    if (!datas || !datas.action || datas.action.length <= 0) return
    const sortByTime = list => list.sort( (a, b) => parseFloat(a.time) > parseFloat(b.time) )
    const stringToVector = string => {
      const extract = string.split(' ')
      return {
        x: parseFloat(extract[0]),
        y: parseFloat(extract[1]),
        z: parseFloat(extract[2])
      }
    }
    const dataToModel = data => ({
      ratio: data.time !== null ? parseFloat(data.time) * 0.01 : -1,
      position: stringToVector(data.position),
      target: data.target ? stringToVector(data.target) : null,
      rotation: data.rotation ? stringToVector(data.rotation) : null
    })
    this.datas = sortByTime(datas.action).map(data => dataToModel(data))
    const first = this.datas[0]
    const last = this.datas[this.datas.length-1]
    if (first.ratio > 0) {
      this.datas.unshift(Object.assign({}, first, { ratio:0 }))
    }
    if (last.ratio < 1) {
      this.datas.push(Object.assign({}, last, { ratio:1 }))
    }
    this.duration = duration
    this.dataIndexCurrent = 0
    this.ratio = 0
    this.offset = { x: 0, y: 0 }
    this.needUpdate = true

    this.dataIntro = datas.intro ? dataToModel(datas.intro) : null
    this.dataOutro = datas.outro ? dataToModel(datas.outro) : null
  }

  tweenBetween (start, end, duration = DEFAULT_DURATION, delay = 0, onComplete = () => {}, easeFunction = Easing.Cubic.InOut) {
    let pos, targ, rot
    const motionProp = { ratio: 0 }
    const update = ratio => {
      pos = end && end.position ? interpolateVector(start.position, end.position, motionProp.ratio) : start.position
      targ = end && end.target ? interpolateVector(start.target, end.target, motionProp.ratio) : start.target
      rot = end && end.rotation ? interpolateVector(start.rotation, end.rotation, motionProp.ratio) : start.rot
      this.setPosition(pos.x, pos.y, pos.z)
      rot ? this.setRotation(rot.x, rot.y, rot.z) : this.setTarget(targ.x, targ.y, targ.z)
    }

    update(0)

    ease(motionProp)
      .to({ ratio: 1 }, duration)
      .easing(easeFunction)
      .delay(delay)
      .onUpdate(o => { update(motionProp.ratio) })
      .onComplete(o => onComplete())
      .start()
  }

  intro (duration = DEFAULT_DURATION, delay = 0) {
    if (!this.dataIntro) return
    const onComplete = () => {
      this.needUpdate = true
      this.smoothRatio = this.ratio
    }
    this.needUpdate = false
    this.tweenBetween(this.dataIntro, this.datas[0], duration, delay, onComplete, Easing.Quartic.Out)
  }

  introReverse (duration = DEFAULT_DURATION, delay = 0) {
    if (!this.dataIntro) return
    const onComplete = () => {
      this.smoothRatio = this.ratio
    }
    this.needUpdate = false
    this.tweenBetween(this.datas[0], this.dataIntro, duration, delay, onComplete, Easing.Quartic.Out)
  }

  outro (duration = DEFAULT_DURATION, delay = 0) {
    this.needUpdate = false
    return new Promise(resolve => {
      const onComplete = () => {
        this.smoothRatio = this.ratio
        resolve()
      }
      if (!this.dataOutro) onComplete()
      const lastData = this.datas[this.datas.length - 1]
      this.tweenBetween(lastData, this.dataOutro, duration, delay, onComplete, Easing.Cubic.In)
    })
  }

  updateFromRatioTime (ratio) {
    const getDataFomId = id => this.datas && this.datas.length > id ? this.datas[id] : null
    // get datas from ratio time
    let data = getDataFomId(this.dataIndexCurrent)
    let dataNext = getDataFomId(this.dataIndexCurrent + 1)
    if (!data) return
    if ( ratio < data.ratio ) {
      this.dataIndexCurrent = 0
      this.updateFromRatioTime(ratio)
      return
    }
    if ( ratio > data.ratio && ratio >= dataNext.ratio ) {
      this.dataIndexCurrent++
      this.updateFromRatioTime(ratio)
      return
    }
    // interpolate position 
    let rangeRatio = dataNext ? getRangeRatio(data.ratio, dataNext.ratio, ratio) : 1
    let pos = dataNext && dataNext.position ? interpolateVector(data.position, dataNext.position, rangeRatio) : data.position
    let targ = dataNext && dataNext.target ? interpolateVector(data.target, dataNext.target, rangeRatio) : data.target
    let rot = dataNext && dataNext.rotation ? interpolateVector(data.rotation, dataNext.rotation, rangeRatio) : data.rotation
    this.setPosition(pos.x, pos.y, pos.z)
    rot ? this.setRotation(rot.x, rot.y, rot.z) : this.setTarget(targ.x, targ.y, targ.z)
  }

  playTween (completeHandler = () => {}, duration = null, easingFunc = Easing.Cubic.InOut) {
    this.dataIndexCurrent = 0
    this.ratio = 0
    ease(this)
    .to({ ratio: 1 }, this.duration)
    .easing(easingFunc)
    .onComplete(() => completeHandler())
    .start()
  }

  playTweenRevert (duration = null, easingFunc = Easing.Cubic.InOut) {
    this.dataIndexCurrent = this.datas.length - 1
    ease(this)
    .to({ ratio: 0 }, duration || this.duration)
    .easing(easingFunc)
    .start()
  }

  update (time, delta, offsetX = 0, offsetY = 0) {
    if (this.needUpdate) {
      // calcul smooth ration animation for camera inertie
      this.smoothRatio += (this.ratio - this.smoothRatio) * 0.05
      // calcul mouse smooth offset camera, more the ratio animation increase less the offset have effect
      this.offset.x += ((offsetX - this.offset.x) * 0.1) * (1 - this.ratio)
      this.offset.y += ((offsetY - this.offset.y) * 0.1) * (1 - this.ratio)
      // update camera position
      this.updateFromRatioTime(this.smoothRatio)
      this.position.x += this.offset.x * 0.03
      this.position.y -= this.offset.y * 0.03
    }
  }

}
export default MotionCam


// WEBPACK FOOTER //
// ./src/scripts/webgl/MotionCam.js